import { Stack } from "@phosphor-icons/react";

const DEMO_LEVELS = [
    { v: "", label: "No demo" },
    { v: "2ft", label: "2 ft cut" },
    { v: "4ft", label: "4 ft cut" },
    { v: "full_gut", label: "Floor-to-ceiling / Full gut" },
];

const WALL_SCOPES = [
    { v: "", label: "All walls" },
    { v: "Exterior wall only", label: "Exterior wall only" },
    { v: "One wall", label: "One wall" },
    { v: "Two walls", label: "Two walls" },
];

export function RoomBuildUp({ rooms, roomTotal, sqftOverride, onAdd, onUpdate, onRemove }) {
    return (
        <div className="mt-4 border border-[var(--rom-border)] rounded-[6px] overflow-hidden" data-testid="room-builder">
            <div className="flex items-center justify-between px-4 py-2.5 bg-[var(--rom-orange-soft)]/30 border-b border-[var(--rom-border)]">
                <div className="flex items-center gap-2">
                    <Stack size={14} weight="bold" className="text-[var(--rom-navy)]" />
                    <span className="text-[12px] font-bold uppercase tracking-[0.08em] text-[var(--rom-text)]">Room-by-room build-up</span>
                    <span className="text-[11px] text-[var(--rom-text-3)]">optional</span>
                </div>
                <span className="text-[11px] text-[var(--rom-text-2)]">Totals automatically into affected sq ft</span>
            </div>
            <div className="overflow-x-auto">
                <table className="rom-table" data-testid="room-table">
                    <thead>
                        <tr>
                            <th>Room / Area</th>
                            <th>Floor / Level</th>
                            <th className="numeric">Affected sq ft</th>
                            <th>Demo level</th>
                            <th>Wall scope</th>
                            <th>Condition</th>
                            <th>Materials affected</th>
                            <th>Notes / damage</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {rooms.length === 0 && (
                            <tr><td colSpan={9} className="text-center text-[var(--rom-text-3)] py-4">No rooms yet — add a room, or dictate a walkthrough with the AI Estimator's mic.</td></tr>
                        )}
                        {rooms.map((r, idx) => (
                            <tr key={r.id} data-testid={`room-row-${idx}`}>
                                <td><input className="input py-1 px-2 text-sm" value={r.name || ""} placeholder="e.g. Main bedroom" onChange={(e) => onUpdate(r.id, "name", e.target.value)} data-testid={`room-name-${idx}`} /></td>
                                <td><input className="input py-1 px-2 text-sm" value={r.floor || ""} placeholder="e.g. Level 1" onChange={(e) => onUpdate(r.id, "floor", e.target.value)} data-testid={`room-floor-${idx}`} /></td>
                                <td className="numeric"><input type="number" min={0} className="input py-1 px-2 text-sm font-mono text-right" value={r.sqft ?? ""} placeholder="0" onChange={(e) => onUpdate(r.id, "sqft", e.target.value === "" ? "" : Number(e.target.value))} data-testid={`room-sqft-${idx}`} /></td>
                                <td>
                                    <select className="select py-1 px-2 text-sm" value={r.demoLevel || ""} onChange={(e) => onUpdate(r.id, "demoLevel", e.target.value)} data-testid={`room-demo-level-${idx}`}>
                                        {DEMO_LEVELS.map((d) => <option key={d.v} value={d.v}>{d.label}</option>)}
                                    </select>
                                </td>
                                <td>
                                    <select className="select py-1 px-2 text-sm" value={WALL_SCOPES.some((w) => w.v === (r.wallScope || "")) ? (r.wallScope || "") : "__custom"} onChange={(e) => onUpdate(r.id, "wallScope", e.target.value === "__custom" ? (r.wallScope || "") : e.target.value)} data-testid={`room-wall-scope-${idx}`}>
                                        {WALL_SCOPES.map((w) => <option key={w.v} value={w.v}>{w.label}</option>)}
                                        {!WALL_SCOPES.some((w) => w.v === (r.wallScope || "")) && <option value="__custom">{r.wallScope}</option>}
                                    </select>
                                </td>
                                <td>
                                    <select className="select py-1 px-2 text-sm" value={r.condition || ""} onChange={(e) => onUpdate(r.id, "condition", e.target.value)} data-testid={`room-condition-${idx}`}>
                                        <option value="">—</option>
                                        {["Light", "Moderate", "Heavy", "Severe"].map((c) => <option key={c} value={c}>{c}</option>)}
                                    </select>
                                </td>
                                <td><input className="input py-1 px-2 text-sm" value={r.materials || ""} placeholder="e.g. baseboard, drywall, carpet and pad" onChange={(e) => onUpdate(r.id, "materials", e.target.value)} data-testid={`room-materials-${idx}`} /></td>
                                <td><input className="input py-1 px-2 text-sm" value={r.notes || ""} placeholder="Damage description" onChange={(e) => onUpdate(r.id, "notes", e.target.value)} data-testid={`room-notes-${idx}`} /></td>
                                <td className="text-right"><button onClick={() => onRemove(r.id)} className="text-[var(--rom-danger)] hover:opacity-70" data-testid={`room-del-${idx}`}>×</button></td>
                            </tr>
                        ))}
                        <tr>
                            <td colSpan={9} className="p-0">
                                <button onClick={onAdd} className="w-full py-2.5 text-xs font-semibold text-[var(--rom-navy)] hover:bg-[var(--rom-orange-soft)]/30 transition-colors" data-testid="room-add-row">+ Add room</button>
                            </td>
                        </tr>
                    </tbody>
                    <tfoot>
                        <tr>
                            <td colSpan={2}>Room build-up total</td>
                            <td className="numeric font-display font-black" data-testid="room-total">{roomTotal.toLocaleString()} sq ft</td>
                            <td colSpan={6} className="text-[var(--rom-text-2)] text-[11px]">
                                {sqftOverride ? "Manual total override is active — room total is not being used." : "Feeding the Total affected sq ft above."}
                            </td>
                        </tr>
                    </tfoot>
                </table>
            </div>
        </div>
    );
}
